// dashboard/src/components/analytics/UsageAnalytics.tsx
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getUsageAnalytics } from '../../services/creditService';
import { formatDateOnly } from '../../utils/dateUtils';

// Note: In a real implementation, you would use a charting library like Chart.js or Recharts
// This is a simplified version using basic HTML/CSS for demonstration

const UsageAnalytics: React.FC = () => {
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });

  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });

  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['usageAnalytics', startDate, endDate, groupBy],
    queryFn: () => getUsageAnalytics(startDate, endDate, groupBy),
    staleTime: 60000,
  });

  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    refetch();
  };

  const handleQuickSelect = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);

    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);

    // Adjust groupBy based on range
    if (days <= 14) {
      setGroupBy('day');
    } else if (days <= 90) {
      setGroupBy('week');
    } else {
      setGroupBy('month');
    }
  };

  if (isLoading && !data) {
    return (
      <div className="bg-white shadow rounded-lg p-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-64 bg-gray-200 rounded w-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow rounded-lg p-6 border-l-4 border-error-500">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Usage Analytics</h3>
        <p className="text-error-500">Error loading analytics. Please try again later.</p>
      </div>
    );
  }

  // Find the maximum cost for scaling the chart
  const maxCost = data?.time_series.reduce((max: number, point: { total_cost: number }) => Math.max(max, point.total_cost), 0) || 0;

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Usage Analytics</h3>
      </div>

      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="mb-4">
          <div className="text-sm font-medium text-gray-700 mb-2">Quick Select</div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => handleQuickSelect(7)}
              className="btn-sm btn-secondary"
            >
              Last 7 Days
            </button>
            <button
              type="button"
              onClick={() => handleQuickSelect(30)}
              className="btn-sm btn-secondary"
            >
              Last 30 Days
            </button>
            <button
              type="button"
              onClick={() => handleQuickSelect(90)}
              className="btn-sm btn-secondary"
            >
              Last 90 Days
            </button>
            <button
              type="button"
              onClick={() => handleQuickSelect(365)}
              className="btn-sm btn-secondary"
            >
              Last Year
            </button>
          </div>
        </div>

        <form onSubmit={handleFilterSubmit} className="flex flex-wrap gap-4">
          <div>
            <label htmlFor="analyticsStartDate" className="block text-xs font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              id="analyticsStartDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="input-field"
            />
          </div>

          <div>
            <label htmlFor="analyticsEndDate" className="block text-xs font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              id="analyticsEndDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="input-field"
            />
          </div>

          <div>
            <label htmlFor="groupBy" className="block text-xs font-medium text-gray-700 mb-1">
              Group By
            </label>
            <select
              id="groupBy"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as 'day' | 'week' | 'month')}
              className="input-field"
            >
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              type="submit"
              className="btn-secondary btn-sm"
            >
              Apply
            </button>
          </div>
        </form>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="text-sm text-gray-500 mb-1">Total Credits Used</div>
            <div className="text-2xl font-bold chart-primary">
              {data?.summary_metrics.total_cost.toLocaleString()}
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="text-sm text-gray-500 mb-1">Most Used Tool</div>
            <div className="text-lg font-semibold text-gray-800">
              {data?.summary_metrics.most_used_tool || 'N/A'}
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="text-sm text-gray-500 mb-1">Most Expensive Category</div>
            <div className="text-lg font-semibold text-gray-800">
              {data?.summary_metrics.most_expensive_category || 'N/A'}
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="text-sm text-gray-500 mb-1">Average Daily Cost</div>
            <div className="text-2xl font-bold chart-primary">
              {data?.summary_metrics.average_daily_cost.toLocaleString()}
            </div>
          </div>
        </div>

        <div className="mb-8">
          <h4 className="text-md font-medium text-gray-700 mb-4">Usage Over Time</h4>

          {data?.time_series.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No usage data available for the selected period
            </div>
          ) : (
            <div className="h-64 relative">
              <div className="absolute inset-0 flex items-end">
                {data?.time_series.map((point: { timestamp: string; total_cost: number }, index: number) => (
                  <div
                    key={index}
                    className="flex-1 flex flex-col items-center justify-end h-full"
                  >
                    <div
                      className="w-full max-w-[30px] progress-primary rounded-t"
                      style={{
                        height: `${maxCost > 0 ? (point.total_cost / maxCost) * 100 : 0}%`,
                        minHeight: point.total_cost > 0 ? '4px' : '0',
                      }}
                      title={`${point.timestamp}: ${point.total_cost} credits`}
                    ></div>
                    <div className="text-xs text-gray-500 mt-1 truncate w-full text-center">
                      {formatTimeLabel(point.timestamp, groupBy)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h4 className="text-md font-medium text-gray-700 mb-4">Category Breakdown</h4>

            {data?.category_breakdown.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No category data available
              </div>
            ) : (
              <div>
                {data?.category_breakdown.map((category: { category: string; cost: number; percentage: number }, index: number) => (
                  <div key={index} className="mb-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-gray-700">{category.category}</span>
                      <span className="text-gray-500">
                        {category.cost.toLocaleString()} credits ({category.percentage}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="progress-primary h-2 rounded-full"
                        style={{ width: `${category.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <h4 className="text-md font-medium text-gray-700 mb-4">Tool Usage</h4>

            {data?.tool_breakdown.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No tool usage data available
              </div>
            ) : (
              <div className="overflow-hidden border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tool
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Count
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cost
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data?.tool_breakdown.map((tool: { tool: string; count: number; cost: number }, index: number) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-2 text-sm text-gray-900">{tool.tool}</td>
                        <td className="px-4 py-2 text-sm text-gray-500 text-right">{tool.count.toLocaleString()}</td>
                        <td className="px-4 py-2 text-sm text-gray-500 text-right">{tool.cost.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to format time labels based on grouping
const formatTimeLabel = (timestamp: string, groupBy: string): string => {
  if (groupBy === 'day') {
    return formatDateOnly(timestamp);
  } else if (groupBy === 'week') {
    // For week format (e.g., "2023-W01"), extract the week number
    const parts = timestamp.split('-W');
    return `W${parts[1]}`;
  } else if (groupBy === 'month') {
    // For month format (e.g., "2023-01"), convert to month name
    const [year, month] = timestamp.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleString('default', { month: 'short', year: '2-digit' });
  }
  return timestamp;
};

export default UsageAnalytics;
